import { getResidentData, getTasksData } from '@/apis/apis';
import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import GlobalAutocomplete from '@/components/material/Autocomplete';
import CustomDrawer from '@/components/material/CustomDrawer';
import CustomRating from '@/components/material/CustomRating';
import RatingView from '@/components/material/RatingView';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { formatDate, formatTableDate } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, List, ListItem, TablePagination, TextField, Typography } from '@mui/material';
import { Pencil, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const feedbackSchema = z.object({
    task: z.object({
        _id: z.string().min(1, "Task is required"),
        title: z.string().optional()
    }),
    residentId: z.object({
        _id: z.string().min(1, "Resident is required"),
        name: z.string().optional()
    }),
    rating: z.number().min(1, "Rating is required").max(5, "Rating must be at most 5"),
    comments: z.string().optional(),
})

const Feedback = () => {
    useDocumentTitle("Feedback");
    const { toast } = useToast();
    const loader = useCustomLoader();
    const [feedback, setFeedback] = useState([]);
    const [counts, setCounts] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [taskData, setTaskData] = useState([]);
    const [searchTask, setSearchTask] = useState('');
    const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
    const [residentData, setResidentData] = useState([]);
    const [searchResident, setSearchResident] = useState('');

    const { control, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: zodResolver(feedbackSchema),
        defaultValues: {
            task: {
                _id: '',
                title: ''
            },
            residentId: {
                _id: '',
                name: ''
            },
            rating: 0,
            comments: '',
        }
    });

    const fetchFeedback = async () => {
        loader(true);
        try {
            const { data } = await axiosInstance.get(`/api/feedback`);
            setFeedback(data?.data);
            setCounts(data?.counts);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            });
        }
        finally {
            loader(false);
        }
    }
    useEffect(() => {
        fetchFeedback();
    }, [])

    const fetchTaskData = async () => {
        const data = await getTasksData({ page: 0, limit: 10, search: searchTask });
        setTaskData(data.tasks);
    }
    const fetchResidentData = async () => {
        const filters = {
            search: searchResident
        }
        const data = await getResidentData({ page: 0, rowsPerPage: 10,filters });
        setResidentData(data.data);
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchTaskData();
        }, searchTask ? 600 : 0);

        return () => clearTimeout(timer);
    }, [searchTask])
    
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchResidentData();
        }, searchResident ? 600 : 0);

        return () => clearTimeout(timer);
    }, [searchResident])

    const feedbackSubmit = async (data: any) => {
        try {
            const payload = {
                ...data,
                task: data.task._id,
                residentId: data.residentId._id
            }
            let response: any = {
                success: false,
                message: ''
            };
            if(selectedFeedback){
                const { data } = await axiosInstance.put(`/api/feedback/${selectedFeedback._id}`, payload);
                response = data;
            }
            else{
                const { data } = await axiosInstance.post('/api/feedback', payload);
                response = data;
            }
            if (response.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: response.message,
                });
                reset();
                setOpenDrawer(false);
                fetchFeedback();
            } else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: response.message || "Something went wrong",
                });
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            });
        }
    }

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Feedback
                </Typography>
                {/* <Button type="primary" onClick={fetchFeedback}>Refresh</Button> */}
                <Button variant='default' onClick={() => setOpenDrawer(true)}><Plus/> Add</Button>
            </Box>
            {
                feedback.length === 0 ? (
                    <Typography variant="body1">No feedback available.</Typography>
                ) : (
                    <List>

                        {feedback.map((fb: any) => (
                            <ListItem key={fb._id} className='mb-4 border p-4 rounded-lg'>
                                <Grid container spacing={2} width="100%">
                                    <Grid container size={{ xs: 12 }}>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <Typography variant="h6">Task: {fb.task ? fb.task.title : 'N/A'}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1"><RatingView value={fb.rating} /></Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1"> {fb.residentId?.firstName} {fb.residentId?.lastName}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }} sx={{ textAlign: 'end' }}>
                                            <Typography variant="body2">{formatDate(fb.createdAt)}</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid size={{ xs: 10 }}>
                                        <Typography variant="body2">Comments: {fb.comments}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 2 }} sx={{ textAlign: 'end' }}>
                                        <button onClick={() => {setOpenDrawer(true); setSelectedFeedback(fb); reset(fb)}}>
                                            <Pencil />
                                        </button>
                                    </Grid>
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                )
            }
            <TablePagination
                component="div"
                count={counts}
                page={page}
                rowsPerPage={limit}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => setLimit(parseInt(e.target.value, 10))}
                sx={{ mt: 2 }}
            />
            {
                openDrawer && <CustomDrawer onClose={() => setOpenDrawer(false)} title={`${selectedFeedback ? 'Edit' : 'Add'} Feedback`}handleSubmit={handleSubmit} onSubmit={feedbackSubmit} >
                    <Box>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="task"
                                    control={control}
                                    render={({ field }) => (
                                        <GlobalAutocomplete
                                            label="Select Task"
                                            options={taskData}
                                            value={field.value}
                                            getOptionLabel={(option: any) => option?.title || ""}
                                            onChange={(newValue: any) => {
                                                field.onChange(newValue);
                                            }}
                                            onInputChange={setSearchTask}
                                            placeholder="Search Task by title"
                                        />
                                    )}
                                />

                                {
                                    errors.task?._id && <Typography variant="body2" color="error">{errors.task._id.message}</Typography>
                                }
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="residentId"
                                    control={control}
                                    render={({ field }) => (
                                        <GlobalAutocomplete
                                            label="Select Resident"
                                            options={residentData}
                                            value={field.value}
                                            getOptionLabel={(option: any) => `${option?.firstName || ''} ${option?.lastName || ''}`}
                                            onChange={(newValue: any) => {
                                                field.onChange(newValue);
                                            }}
                                            onInputChange={setSearchResident}
                                            placeholder="Search Resident by title"
                                        />
                                    )}
                                />

                                {
                                    errors.residentId?._id && <Typography variant="body2" color="error">{errors.residentId._id.message}</Typography>
                                }
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="rating"
                                    control={control}
                                    render={({ field }) => (
                                        <CustomRating title="Rating" field={field} errors={errors} />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="comments"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Comments"
                                            fullWidth
                                            size='small'
                                            multiline
                                            rows={4}
                                            error={!!errors.comments}
                                            helperText={errors.comments ? errors.comments.message : ''}
                                            placeholder='Please Enter Your Comments'
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </CustomDrawer>
            }

        </div>
    )
}

export default Feedback